import { useEffect, useRef, useState } from 'react';
import * as BABYLON from 'babylonjs';

interface RacingGameProps {
  activeCurrency: string;
  onBetPlaced: (amount: number) => Promise<boolean>;
  onWinClaimed: (multiplier: number) => void;
  balance: number;
}

export default function RacingGame({ activeCurrency, onBetPlaced, onWinClaimed, balance }: RacingGameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [betAmount, setBetAmount] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [multiplier, setMultiplier] = useState(1.0);

  const engineRef = useRef<BABYLON.Engine | null>(null);
  const carRef = useRef<BABYLON.Mesh | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // 1. 初始化 BabylonJS 引擎与场景
    const engine = new BABYLON.Engine(canvasRef.current, true);
    engineRef.current = engine;

    const scene = new BABYLON.Scene(engine);
    scene.clearColor = new BABYLON.Color4(0.05, 0.06, 0.1, 1.0); // 融入大厅的深色调背景

    // 2. 相机配置 (追随视角)
    const camera = new BABYLON.FreeCamera('camera', new BABYLON.Vector3(0, 5, -12), scene);
    camera.setTarget(new BABYLON.Vector3(0, 1, 0));

    // 3. 光照
    const light = new BABYLON.HemisphericLight('light', new BABYLON.Vector3(0, 1, 0), scene);
    light.intensity = 0.7;

    // 4. 创建 3D 星空科幻赛道 (用网格拉伸)
    const road = BABYLON.MeshBuilder.CreateBox('road', { width: 6, height: 0.2, depth: 200 }, scene);
    road.position.z = 80;
    const roadMat = new BABYLON.StandardMaterial('roadMat', scene);
    roadMat.diffuseColor = new BABYLON.Color3(0.1, 0.15, 0.25);
    roadMat.emissiveColor = new BABYLON.Color3(0.02, 0.05, 0.1);
    road.material = roadMat;

    // 赛道边缘装饰霓虹彩带
    const leftRail = BABYLON.MeshBuilder.CreateBox('leftRail', { width: 0.15, height: 0.4, depth: 200 }, scene);
    leftRail.position.set(-3, 0.2, 80);
    const railMat = new BABYLON.StandardMaterial('railMat', scene);
    railMat.emissiveColor = new BABYLON.Color3(0.0, 1.0, 1.0); // 青色霓虹灯
    leftRail.material = railMat;

    const rightRail = leftRail.clone('rightRail');
    rightRail.position.x = 3;

    // 5. 简易 3D 科幻飞车
    const car = BABYLON.MeshBuilder.CreateBox('car', { width: 1.2, height: 0.6, depth: 2.2 }, scene);
    car.position.set(0, 0.4, 0);
    const carMat = new BABYLON.StandardMaterial('carMat', scene);
    carMat.diffuseColor = new BABYLON.Color3(0.9, 0.1, 0.6); // 桃红色飞车
    carMat.emissiveColor = new BABYLON.Color3(0.4, 0.0, 0.2);
    car.material = carMat;
    carRef.current = car;

    // 6. 3D 粒子推进器火花
    const particleSystem = new BABYLON.ParticleSystem('particles', 500, scene);
    particleSystem.particleTexture = new BABYLON.Texture('data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2210%22 height=%2210%22><circle cx=%225%22 cy=%225%22 r=%224%22 fill=%22%23ff007c%22/></svg>', scene);
    particleSystem.emitter = car;
    particleSystem.minEmitBox = new BABYLON.Vector3(-0.4, -0.2, -1.1);
    particleSystem.maxEmitBox = new BABYLON.Vector3(0.4, 0.2, -1.1);
    particleSystem.color1 = new BABYLON.Color4(1.0, 0.0, 0.5, 1.0);
    particleSystem.color2 = new BABYLON.Color4(0.5, 0.0, 1.0, 1.0);
    particleSystem.minSize = 0.1;
    particleSystem.maxSize = 0.3;
    particleSystem.minLifeTime = 0.2;
    particleSystem.maxLifeTime = 0.6;
    particleSystem.emitRate = 120;
    particleSystem.gravity = new BABYLON.Vector3(0, -1, 0);
    particleSystem.direction1 = new BABYLON.Vector3(0, 0, -2);
    particleSystem.start();

    // 渲染主循环
    let isMoving = false;
    let speed = 0;
    let distance = 0;

    engine.runRenderLoop(() => {
      if (isMoving && carRef.current) {
        // 车辆在赛道上持续前进
        speed += 0.01;
        distance += speed * 0.1;
        carRef.current.position.z = distance;
        
        // 摄像机镜头追击抖动效果
        camera.position.z = carRef.current.position.z - 12 + Math.sin(Date.now() * 0.05) * 0.05;
        camera.position.y = carRef.current.position.y + 4 + Math.sin(Date.now() * 0.02) * 0.03;
        
        // 汽车左右微摆动模拟颠簸
        carRef.current.rotation.y = Math.sin(Date.now() * 0.01) * 0.02;
      } else if (carRef.current) {
        // 空闲漂浮动效
        carRef.current.position.y = 0.4 + Math.sin(Date.now() * 0.005) * 0.04;
        camera.position.set(0, 5, -12);
      }
      
      scene.render();
    });

    // 暴露控制器接口
    (scene as any).startRace = () => {
      isMoving = true;
      speed = 0.5;
      distance = 0;
    };

    (scene as any).stopRace = () => {
      isMoving = false;
      const finalMultiplier = 1.0 + (distance / 50.0);
      return finalMultiplier;
    };

    // 窗口自适应
    const resizeHandler = () => engine.resize();
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      engine.dispose();
    };
  }, []);

  const handleStart = async () => {
    if (balance < betAmount) {
      alert('余额不足！');
      return;
    }

    const success = await onBetPlaced(betAmount);
    if (success && engineRef.current) {
      setIsPlaying(true);
      setMultiplier(1.0);

      const scene = engineRef.current.scenes[0];
      if (scene) {
        (scene as any).startRace();
      }

      // 实时计算下注过程中的倍率递增
      let multInterval = setInterval(() => {
        setIsPlaying(playing => {
          if (!playing) {
            clearInterval(multInterval);
            return false;
          }
          setMultiplier(prev => Math.round((prev + 0.08) * 100) / 100);
          return true;
        });
      }, 100);

      // 模拟撞车或掉下悬崖
      const crashDelay = Math.random() * 6000 + 4000;
      setTimeout(() => {
        setIsPlaying(playing => {
          if (playing) {
            clearInterval(multInterval);
            alert('💥 赛车偏离轨道失控爆炸了！下注输掉！');
            onWinClaimed(0);
            if (engineRef.current?.scenes[0]) {
              (engineRef.current.scenes[0] as any).stopRace();
            }
          }
          return false;
        });
      }, crashDelay);
    }
  };

  const handleCashout = () => {
    if (!isPlaying || !engineRef.current) return;
    
    const scene = engineRef.current.scenes[0];
    if (scene) {
      const finalMult = (scene as any).stopRace();
      onWinClaimed(finalMult);
      setIsPlaying(false);
      alert(`🎉 车辆成功安全制动！获得 ${finalMult.toFixed(2)}x 收益！`);
    }
  };

  return (
    <div className="glass-card" style={{ padding: '12px' }}>
      <h3 style={{ margin: '8px 12px', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--color-accent)' }}></span>
        9F Racing 3D (Babylon.js + WebGPU 赛车)
      </h3>
      
      <div className="game-frame" style={{ height: '240px' }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', outline: 'none' }} />
      </div>

      <div style={{ display: 'flex', gap: '12px', padding: '0 12px 12px' }}>
        {!isPlaying ? (
          <>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>投注额 ({activeCurrency.toUpperCase()})</label>
              <input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  padding: '10px',
                  borderRadius: '8px',
                  marginTop: '4px',
                  fontFamily: 'var(--font-primary)'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
              <button className="btn-neon" onClick={handleStart} style={{ width: '100%', padding: '12px', background: 'linear-gradient(90deg, var(--color-accent), var(--color-primary))' }}>
                加速狂飙
              </button>
            </div>
          </>
        ) : (
          <button 
            className="btn-neon" 
            onClick={handleCashout} 
            style={{ width: '100%', padding: '12px', background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))' }}
          >
            安全制动拉手刹 ({multiplier.toFixed(2)}x)
          </button>
        )}
      </div>
    </div>
  );
}
